import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// Map Supabase user + profile to the app-shaped user the rest of the UI expects.
const toAppUser = (user, profile) => {
  if (!user) return null;
  return {
    id: user.id,
    email: user.email || (profile?.is_guest ? `guest_${user.id.slice(0, 8)}@tpocare.ai` : ''),
    name: profile?.name || user.user_metadata?.name || (profile?.is_guest ? 'Guest User' : ''),
    is_guest: Boolean(profile?.is_guest ?? user.is_anonymous ?? false),
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = async (sbUser) => {
    if (!sbUser) return null;
    const { data } = await supabase
      .from('profiles')
      .select('name, is_guest')
      .eq('id', sbUser.id)
      .maybeSingle();
    return data;
  };

  const hydrate = async (sbSession) => {
    setSession(sbSession);
    if (sbSession?.user) {
      const profile = await loadProfile(sbSession.user);
      setUser(toAppUser(sbSession.user, profile));
    } else {
      setUser(null);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      await hydrate(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
      await hydrate(newSession);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    const profile = await loadProfile(data.user);
    const appUser = toAppUser(data.user, profile);
    setUser(appUser);
    setSession(data.session);
    return appUser;
  };

  const register = async (email, password, name) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    });
    if (error) throw error;
    // If email confirmation is OFF (recommended for this app), data.session is present.
    if (data.session) {
      const profile = await loadProfile(data.user);
      const appUser = toAppUser(data.user, profile);
      setUser(appUser);
      setSession(data.session);
      return appUser;
    }
    // Otherwise the user must verify their email before they can sign in.
    return toAppUser(data.user, { name, is_guest: false });
  };

  const guestLogin = async () => {
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error) throw error;
    // Profile is created via DB trigger; pull it.
    const profile = await loadProfile(data.user);
    const appUser = toAppUser(data.user, profile ?? { name: 'Guest User', is_guest: true });
    setUser(appUser);
    setSession(data.session);
    return appUser;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  // Backwards-compat helper for code that read fetchUser() before.
  const fetchUser = async () => {
    const { data } = await supabase.auth.getUser();
    if (data.user) {
      const profile = await loadProfile(data.user);
      const appUser = toAppUser(data.user, profile);
      setUser(appUser);
      return appUser;
    }
    return null;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token: session?.access_token ?? null,
        session,
        loading,
        login,
        register,
        guestLogin,
        logout,
        fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
